import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  copyAutoCreateEmailTemplate,
  copyEmailTemplate,
  getAutoCreateEmailTemplates,
  getEmailTemplates,
  syncAutoCreateEmailTemplates,
  syncEmailTemplates,
} from '../../services/portalServices';
import styled from 'styled-components';
import type {
  AutoCreateEmailTemplateStatus,
  EmailTemplateStatus,
  PartnerName,
  SyncStatus,
} from '../../client/portal/types.gen';
import Button from '../../components/Button';
import ContentWrapper from '../../components/ContentWrapper';
import TabNavigation from '../../components/TabNavigation';
import { useNotification } from '../../context/notificationContext';

type Template = EmailTemplateStatus | AutoCreateEmailTemplateStatus;

// A template's status is keyed by partner name; the partners it has been
// copied to are the ones whose status is not 'NotCopied'.
type CopiedStatus = Exclude<SyncStatus, 'NotCopied'>;

const isCopied = (
  entry: [PartnerName, SyncStatus]
): entry is [PartnerName, CopiedStatus] => entry[1] !== 'NotCopied';

const isNotCopied = (entry: [PartnerName, SyncStatus]) =>
  entry[1] === 'NotCopied';

type TemplateItemProps<T extends Template> = {
  template: T;
  onCopyToPartner: (
    partnerName: PartnerName,
    templateKey: T['key']
  ) => Promise<void>;
};

const TemplateItem = <T extends Template>({
  template,
  onCopyToPartner,
}: TemplateItemProps<T>) => {
  const handleCopyToChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    await onCopyToPartner(event.target.value, template.key);
  };

  const statusEntries: [PartnerName, SyncStatus][] = Object.entries(
    template.status
  );

  return (
    <TemplateItemWrapper key={template.key}>
      <h3>{template.name}</h3>
      <select onChange={handleCopyToChange}>
        <option>-- Select partner to copy template to --</option>
        {statusEntries.filter(isNotCopied).map(([partnerName]) => (
          <option key={partnerName} value={partnerName}>
            {partnerName}
          </option>
        ))}
      </select>
      <PartnerList>
        {statusEntries.filter(isCopied).map(([partnerName, status]) => (
          <CopiedItem key={partnerName} status={status}>
            <span>{partnerName}</span>
            <Status>{status}</Status>
          </CopiedItem>
        ))}
      </PartnerList>
    </TemplateItemWrapper>
  );
};

type PaneProps<T extends Template> = {
  fetchTemplates: () => Promise<T[]>;
  copyTemplate: (
    partnerName: PartnerName,
    templateKeys: T['key'][]
  ) => Promise<void>;
  syncTemplates: () => Promise<void>;
  copySuccessLabel: string;
  copyErrorLabel: string;
  fetchErrorLabel: string;
  syncSuccessLabel: string;
  syncErrorLabel: string;
};

const TemplatesPane = <T extends Template>({
  fetchTemplates,
  copyTemplate,
  syncTemplates,
  copySuccessLabel,
  copyErrorLabel,
  fetchErrorLabel,
  syncSuccessLabel,
  syncErrorLabel,
}: PaneProps<T>) => {
  const [templates, setTemplates] = useState<T[]>([]);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const { addNotification, removeNotificationWithDelay } = useNotification();

  const loadTemplates = async () => {
    try {
      setTemplates(await fetchTemplates());
    } catch (error) {
      removeNotificationWithDelay(addNotification(fetchErrorLabel, 'alert'));
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCopyToPartner = async (
    partnerName: PartnerName,
    templateKey: T['key']
  ) => {
    try {
      await copyTemplate(partnerName, [templateKey]);
      loadTemplates();
      removeNotificationWithDelay(
        addNotification(`${copySuccessLabel} ${partnerName}`, 'success')
      );
    } catch (error) {
      removeNotificationWithDelay(addNotification(copyErrorLabel, 'alert'));
    }
  };

  const handleSyncClick = async () => {
    try {
      setIsSyncing(true);
      await syncTemplates();
      loadTemplates();
      removeNotificationWithDelay(
        addNotification(syncSuccessLabel, 'success')
      );
    } catch (error) {
      removeNotificationWithDelay(addNotification(syncErrorLabel, 'alert'));
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <PaneWrapper>
      <ActionWrapper>
        <Button theme='blue' onClick={handleSyncClick} disabled={isSyncing}>
          Sync copied templates
        </Button>
      </ActionWrapper>
      <TemplateList>
        {templates.map((template) => (
          <TemplateItem
            key={template.key}
            template={template}
            onCopyToPartner={handleCopyToPartner}
          />
        ))}
      </TemplateList>
    </PaneWrapper>
  );
};

type Tab = 'regular' | 'auto-create';

const TemplatesView = () => {
  const [tab, setTab] = useState<Tab>('regular');
  const navigate = useNavigate();

  const tabs: Tab[] = ['regular', 'auto-create'];

  const handleGoToEditTemplateClick = () =>
    navigate(
      `/partner?url=${encodeURIComponent(
        'https://templates.eirpartners.se/claims'
      )}&redirect=${encodeURIComponent('/email-templates')}`
    );

  return (
    <ContentWrapper>
      <header>
        <h1>E-mail templates</h1>
      </header>
      <ActionWrapper>
        <Button theme='blue' onClick={handleGoToEditTemplateClick}>
          Edit e-mail templates
        </Button>
      </ActionWrapper>
      <TabNavigation
        size='medium'
        selectedIndex={tabs.indexOf(tab)}
        onClick={(index) => setTab(tabs[index])}
        items={[
          { content: <span>Regular</span> },
          { content: <span>Auto-create</span> },
        ]}
      />
      {tab === 'regular' ? (
        <TemplatesPane
          fetchTemplates={getEmailTemplates}
          copyTemplate={copyEmailTemplate}
          syncTemplates={syncEmailTemplates}
          copySuccessLabel='E-mail templated copied to'
          copyErrorLabel='Could not copy e-mail template.'
          fetchErrorLabel='Could not fetch e-mail templates.'
          syncSuccessLabel='E-mail templates synced'
          syncErrorLabel='Could not sync e-mail templates.'
        />
      ) : (
        <TemplatesPane
          fetchTemplates={getAutoCreateEmailTemplates}
          copyTemplate={copyAutoCreateEmailTemplate}
          syncTemplates={syncAutoCreateEmailTemplates}
          copySuccessLabel='Auto-create template copied to'
          copyErrorLabel='Could not copy auto-create template.'
          fetchErrorLabel='Could not fetch auto-create templates.'
          syncSuccessLabel='Auto-create templates synced'
          syncErrorLabel='Could not sync auto-create templates.'
        />
      )}
    </ContentWrapper>
  );
};

export default TemplatesView;

const PaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TemplateList = styled.ul`
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TemplateItemWrapper = styled.li`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #cdcdcd;
  padding: 1rem;
  width: 32rem;
  background-color: #fff;
`;

const PartnerList = styled.ul`
  padding: 0;
  list-style: none;
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const CopiedItem = styled.li<{ status: CopiedStatus }>`
  display: flex;
  flex-direction: column;
  gap: 0.0625rem;
  background-color: ${({ status }) =>
    status === 'Outdated' ? '#fff3cd' : '#62d095'};
  color: ${({ status }) => (status === 'Outdated' ? '#856404' : '#fff')};
  border-radius: 0.25rem;
  padding: 0.125rem 0.25rem;
`;

const Status = styled.span`
  font-size: 0.75rem;
`;

const ActionWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;
